import { styled } from 'styled-components';
import { darken, lighten } from 'polished';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from '@/redux';
import { Star } from '@/components/uielements/icon/star';
import { getFontStyles } from '@/redux/cmsConfig/utils';
import { Render } from '@/components/uielements/render';
import { isDark, deriveReferrer } from '@/utils/global';
import { supportsLoyalty } from '@/utils/loyalty';
import { selectCart } from '@/redux/basket/reducer';
import { ROUTES } from '@/constants/routes';
import globalActions from '@/redux/global/actions';

interface LoyaltyBadgeType {
  type: 'default' | 'success';
  includeLinks?: boolean;
}
interface LoyaltyBadgeProps extends LoyaltyBadgeType {
  style?: React.CSSProperties;
}
export const LoyaltyBadge = ({
  type = 'default',
  style,
  includeLinks = false,
}: LoyaltyBadgeProps) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { subtotal } = useSelector(selectCart);
  const me = useSelector((state) => state.app.me.data);
  const loggedIn = Boolean(me?.id);
  const checkoutBasket = useSelector((state) => state.app.basket.checkoutBasket);
  const organization = useSelector((state) => state.app.organization);
  const referrer = deriveReferrer(router);
  const total = Math.max(
    0,
    (checkoutBasket?.subtotal ?? subtotal ?? 0) - (checkoutBasket?.promo_discount ?? 0),
  );

  const handleLinkClick = () => {
    dispatch(globalActions.toggleBasket(false));
  };

  return (
    <Render condition={Boolean(total > 0 && supportsLoyalty(organization))}>
      <StyledBadge type={type} style={style}>
        <StyledBackgroundStar type={type}>
          <Star
            title="Loyalty Star Icon"
            style={{ color: '#fff', width: '18px', height: '18px' }}
          />
        </StyledBackgroundStar>
        <StyledBadgeContent>
          <Render condition={loggedIn}>
            <StyledBadgeTitle>
              You&apos;re getting {Math.round(total / 100)} loyalty points with this purchase.
            </StyledBadgeTitle>
            <StyledBadgeDescription>Points will be added after your order.</StyledBadgeDescription>
          </Render>
          <Render condition={!loggedIn}>
            <StyledBadgeTitle>
              Want to earn {Math.round(total / 100)} points with this order?
            </StyledBadgeTitle>
            <Render condition={includeLinks}>
              <StyledBadgeDescription>
                Just{' '}
                <Link href={`${ROUTES.LOGIN}?referrer=${referrer}`} passHref legacyBehavior>
                  <StyledLink onClick={handleLinkClick}>sign in</StyledLink>
                </Link>{' '}
                or{' '}
                <Link href={`${ROUTES.SIGN_UP}?referrer=${referrer}`} passHref legacyBehavior>
                  <StyledLink onClick={handleLinkClick}>join</StyledLink>
                </Link>{' '}
                by setting a password — your account is ready.
              </StyledBadgeDescription>
            </Render>
            <Render condition={!includeLinks}>
              <StyledBadgeDescription>
                Just sign in or join by setting a password — your account is ready.
              </StyledBadgeDescription>
            </Render>
          </Render>
        </StyledBadgeContent>
      </StyledBadge>
    </Render>
  );
};

const StyledBadge = styled.div<LoyaltyBadgeType>`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background-color: ${({ theme, type }) => {
    const isDarkBg = theme.checkout.background_color && isDark(theme.checkout.background_color);
    const color = {
      default: darken(0.06, theme.checkout.border_color ?? '#e7e7e7'),
      success: isDarkBg
        ? darken(0.58, theme.buttons.primary_button_background_color)
        : lighten(0.58, theme.buttons.primary_button_background_color),
    };
    return color[type];
  }};
  border-radius: 8px;
  padding: 6px 8px;
  border: 1px solid
    ${({ theme, type }) => {
      const color = {
        default: theme.checkout.border_color,
        success: theme.buttons.primary_button_border_color,
      };
      return color[type];
    }};
`;

const StyledBackgroundStar = styled.div<LoyaltyBadgeType>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme, type }) => {
    const color = {
      default: darken(0.45, theme.global.body_color),
      success: theme.buttons.primary_button_background_color,
    };
    return color[type];
  }};
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 50%;
`;

const StyledBadgeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const StyledBadgeTitle = styled.p`
  margin: 0;
  color: ${({ theme }) => getFontStyles(theme.checkout.secondary_font).color};
  font-weight: 700;
  font-size: 13px;
  line-height: 14px;
`;

const StyledBadgeDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => getFontStyles(theme.checkout.secondary_font).color};
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
`;

const StyledLink = styled.a`
  color: ${({ theme }) => theme.buttons.primary_button_background_color};
  text-decoration: underline;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => darken(0.1, theme.buttons.primary_button_background_color)};
  }
`;
